/**
 * A aba "Consulta" como lista (#1011, protótipo do Claude Design).
 *
 * Cada consulta com a data e hora por extenso, médico e especialidade, os
 * códigos do SISAC e o selo; "Editar" em cada uma e "Nova consulta" no topo. A
 * recém-marcada ou recém-salva vem destacada, com o aviso verde em cima.
 *
 * Só tela: quem a monta passa as consultas e decide o que os botões fazem
 * (ver `lib/consultas-na-tela.ts`).
 */

import type { ReactNode } from 'react';
import { Botao, Icone, Selo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import type { ConsultaNaLista } from '../lib/consultas-na-tela';
import './lista-de-consultas.css';

export function ListaDeConsultas({
  consultas,
  podeEditar,
  agendaConfigurada,
  aviso = null,
  destaqueId = null,
  aoNovaConsulta,
  aoEditar,
  aoCancelar,
}: {
  consultas: readonly ConsultaNaLista[];
  /** Quem não edita a oportunidade vê a lista sem "Nova consulta" e sem "Editar". */
  podeEditar: boolean;
  /** Sem as operações da agenda, o aviso toma o lugar de "Nova consulta". */
  agendaConfigurada: boolean;
  /** "Consulta marcada no SISAC." ou "Consulta salva.", depois de voltar à lista. */
  aviso?: string | null;
  /** A consulta que acabou de mudar, para o olho achar. */
  destaqueId?: string | null;
  aoNovaConsulta: () => void;
  aoEditar: (id: string) => void;
  /** Cancelar a consulta marcada no SISAC (Fase 3); ausente sem a parte ligada. */
  aoCancelar?: (id: string) => void;
}): ReactNode {
  const podeMarcar = podeEditar && agendaConfigurada;
  return (
    <div className="lista-de-consultas">
      {aviso && podeEditar && (
        <p className="lista-de-consultas__aviso" role="status">
          <Icone nome="check" />
          {aviso}
        </p>
      )}
      <section className="lista-de-consultas__bloco" aria-labelledby="lista-de-consultas-titulo">
        <div className="lista-de-consultas__topo">
          <h3 className="lista-de-consultas__titulo" id="lista-de-consultas-titulo">
            Consultas
          </h3>
          {podeMarcar && (
            <Botao variante="primario" onClick={aoNovaConsulta}>
              <Icone nome="mais" />
              Nova consulta
            </Botao>
          )}
        </div>

        {podeEditar && !agendaConfigurada && (
          <Faixa tom="atencao">
            A agenda da clínica ainda não está ligada ao SISAC nesta organização. Fale com o
            suporte.
          </Faixa>
        )}

        {consultas.length === 0 ? (
          <div className="lista-de-consultas__vazio">
            <Icone nome="calendario" />
            <p className="lista-de-consultas__texto-vazio">
              Nenhuma consulta marcada para este paciente.
            </p>
            {podeMarcar && (
              <p className="lista-de-consultas__apoio">
                Use "Nova consulta" para escolher o médico e marcar no SISAC.
              </p>
            )}
          </div>
        ) : (
          <ul className="lista-de-consultas__lista">
            {consultas.map((consulta) => (
              <ItemDaLista
                key={consulta.id}
                consulta={consulta}
                destaque={consulta.id === destaqueId}
                podeEditar={podeEditar}
                aoEditar={() => aoEditar(consulta.id)}
                {...(aoCancelar && consulta.marcada
                  ? { aoCancelar: () => aoCancelar(consulta.id) }
                  : {})}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ItemDaLista({
  consulta,
  destaque,
  podeEditar,
  aoEditar,
  aoCancelar,
}: {
  consulta: ConsultaNaLista;
  destaque: boolean;
  podeEditar: boolean;
  aoEditar: () => void;
  aoCancelar?: () => void;
}): ReactNode {
  const quando = consulta.quando ?? 'Sem data e hora';
  return (
    <li
      className={
        destaque
          ? 'lista-de-consultas__item lista-de-consultas__item--nova'
          : 'lista-de-consultas__item'
      }
    >
      <div className="lista-de-consultas__texto">
        <strong>{quando}</strong>
        <span>
          {consulta.medico?.nome ?? 'Sem médico'} ·{' '}
          {consulta.especialidade?.nome ?? 'Sem especialidade'}
        </span>
        {(consulta.codigoDoPaciente || consulta.codigoDaAgenda) && (
          <span className="lista-de-consultas__meta">
            Paciente no SISAC{' '}
            <span className="lista-de-consultas__codigo">
              {consulta.codigoDoPaciente ?? 'não informado'}
            </span>{' '}
            · Agenda no SISAC{' '}
            <span className="lista-de-consultas__codigo">
              {consulta.codigoDaAgenda ?? 'não informado'}
            </span>
          </span>
        )}
      </div>
      <div className="lista-de-consultas__direita">
        <Selo tom={consulta.marcada ? 'ok' : 'neutro'}>
          {consulta.marcada ? 'Marcada no SISAC' : 'Não marcada'}
        </Selo>
        {podeEditar && (
          <Botao
            variante="secundario"
            miudo
            onClick={aoEditar}
            aria-label={`Editar a consulta de ${quando}`}
          >
            Editar
          </Botao>
        )}
        {podeEditar && aoCancelar && (
          <Botao
            // Vermelho: cancelar libera a vaga no SISAC (#1187).
            variante="perigo"
            miudo
            onClick={aoCancelar}
            aria-label={`Cancelar a consulta de ${quando}`}
          >
            Cancelar
          </Botao>
        )}
      </div>
    </li>
  );
}
